// ---------------------------------------------------------------------------
//  LE CATENE DI UNA GIORNATA: chi porta a casa ogni driver.
//
//  Una corsa «a catena» ha un solo operatore: il driver porta l'auto a
//  destinazione e lì resta, senza mezzo. Da lì deve succedere una di queste
//  cose, altrimenti il driver rimane a piedi:
//
//   - CHIUDE: la corsa lo riporta dove era partita la sua catena;
//   - PROSEGUE: un'altra corsa a catena riparte da lì, dopo il suo arrivo, e
//     la guida lui (è la corsa successiva della catena); oppure riparte da lì
//     uno SPOSTAMENTO SINGOLO, che guida lui: A → B a catena, poi B → C
//     singolo. Il jolly di quel singolo non viaggia con lui: lo raggiunge
//     dalla partenza della catena e ce lo riporta (C → A), lo organizza Simba;
//   - A BORDO: rientra come passeggero su un'altra corsa a catena che parte
//     da lì dopo il suo arrivo (al massimo 4 passeggeri per auto) e lo
//     riporta da dove era partito;
//   - COL JOLLY: rientra con il jolly di uno spostamento SINGOLO che prosegue
//     una catena, se arriva dove arriva quel singolo, non più tardi, ed è
//     partito dalla stessa base (il jolly riporta tutti alla base).
//
//  ANDATA A BORDO, il contrario del rientro: una sola auto va da A a B, da B
//  ne tornano due. Il driver della seconda auto arriva a B come passeggero
//  della prima corsa (`andata`): la sua catena parte da A, dove parte quella
//  corsa, e tornando lì si chiude. Si sale solo su una corsa a catena dello
//  stesso spostamento, che arriva dove parte questa e non dopo; i posti sono
//  gli stessi 4 di chi ci rientra a bordo.
//
//  Se non succede niente di questo la corsa è SCOPERTA. Non esiste «rientra
//  in un altro modo» per una corsa a catena: chi non ha un'altra corsa per
//  tornare fa uno spostamento singolo (anche come corsa che prosegue la
//  catena, vedi sopra). Una corsa singola non lascia nessuno a
//  piedi: il jolly riporta il driver (o, se lo decide Simba, il driver rientra
//  con i mezzi pubblici: `mezzi`).
//
//  ⚠️ COPIA della logica del backend (`src/Journey/catene.ts`), che rifiuta il
//  salvataggio con le stesse regole: se si cambia lì, si cambia qui (e nel
//  gemello dell'altro gestionale).
//
//  Corsa: { id, da, a, inizio, fine, catena, padre?, rientro?, andata?,
//           esterna?, postiPresi?, origine?, mezzi? } — `da`/`a` chiavi di luogo
//  (chiaveLuogo), `inizio`/`fine` confrontabili (minuti o ms), `rientro` = id
//  della corsa di rientro ('?' = scelta da fare: la corsa resta scoperta e
//  non prosegue catene).
// ---------------------------------------------------------------------------

export const MAX_PASSEGGERI = 4;

const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/** Il luogo come chiave: indirizzo e città (le sedi salvate non hanno id). */
export const chiaveLuogo = (p) => (p ? `${norm(p.address)}|${norm(p.city)}` : '');

export function analizzaCatene(corse) {
  const perId = new Map(corse.map((c) => [c.id, c]));
  const ordine = new Map(corse.map((c, i) => [c.id, i]));
  const ora = (v) => (v === null || v === undefined || v === '' ? null : v);
  const perOra = [...corse].sort((x, y) => {
    const a = ora(x.inizio) ?? Number.MAX_SAFE_INTEGER;
    const b = ora(y.inizio) ?? Number.MAX_SAFE_INTEGER;
    return (a < b ? -1 : a > b ? 1 : 0) || ordine.get(x.id) - ordine.get(y.id);
  });

  const padre = new Map();
  const figlio = new Map();
  const radice = new Map();
  const origine = (id, visti = new Set()) => {
    const c = perId.get(id);
    if (c?.esterna) return c.origine;
    const r = perId.get(radice.get(id) ?? id);
    // Arrivato a bordo di un'altra corsa: la sua catena parte da dove parte
    // quella (il passeggero era con quel driver dall'inizio).
    if (r?.andata && !visti.has(r.id)) {
      const t = perId.get(r.andata);
      if (!motivoAndata(r, t)) return origine(t.id, new Set([...visti, r.id]));
    }
    return r?.da;
  };
  const chiude = (c) => c.catena && !!c.a && c.a === origine(c.id);

  for (const c of perOra) {
    if (c.esterna) {
      radice.set(c.id, c.id);
      continue;
    }
    let p;
    if (!c.catena) {
      // Un singolo può proseguire la catena del suo padre esplicito.
      const q = c.padre ? perId.get(c.padre) : undefined;
      if (q && q.catena && !q.esterna && !q.rientro && !figlio.has(q.id)) p = q;
    } else {
      if (c.padre && perId.get(c.padre) && !figlio.has(c.padre)) {
        p = perId.get(c.padre);
      } else if (!c.andata) {
        // Chi arriva a bordo inizia una catena sua: non ne prosegue un'altra.
        let migliore;
        for (const q of perOra) {
          if (q === c) break;
          if (q.esterna || !q.catena || figlio.has(q.id) || q.rientro) continue;
          if (!q.a || q.a !== c.da || chiude(q)) continue;
          if (ora(q.fine) !== null && ora(c.inizio) !== null && q.fine > c.inizio) continue;
          if (!migliore || (q.fine ?? 0) > (migliore.fine ?? 0)) migliore = q;
        }
        p = migliore;
      }
    }
    if (p) {
      padre.set(c.id, p.id);
      figlio.set(p.id, c.id);
      radice.set(c.id, radice.get(p.id) ?? p.id);
    } else {
      radice.set(c.id, c.id);
    }
  }

  // Un driver rimasto senza rientro prosegue con lo spostamento SINGOLO che
  // riparte da dove è arrivato, dopo il suo arrivo (il primo): lo guida lui.
  // Solo per chi resterebbe a piedi, così le catene che si chiudevano già
  // restano come sono.
  for (const q of perOra) {
    if (q.esterna || !q.catena || figlio.has(q.id) || q.rientro || !q.a || chiude(q)) continue;
    const s = perOra.find((c) => !c.esterna && !c.catena && !padre.has(c.id) && c.da === q.a
      && !(ora(q.fine) !== null && ora(c.inizio) !== null && q.fine > c.inizio));
    if (!s) continue;
    padre.set(s.id, q.id);
    figlio.set(q.id, s.id);
    radice.set(s.id, radice.get(q.id) ?? q.id);
  }

  const passeggeri = new Map();
  const rientriNonValidi = new Map();
  const stato = new Map();
  const scoperte = [];

  // Prima chi arriva a bordo: occupa i posti della corsa che lo porta, gli
  // stessi di chi ci rientra.
  const andate = new Map();
  const andateNonValide = new Map();
  for (const c of perOra) {
    if (c.esterna || !c.catena || !c.andata) continue;
    const t = perId.get(c.andata);
    const motivo = motivoAndata(c, t);
    const presi = (andate.get(c.andata)?.length ?? 0) + (t?.postiPresi ?? 0);
    if (!motivo && presi < MAX_PASSEGGERI) {
      andate.set(c.andata, [...(andate.get(c.andata) ?? []), c.id]);
    } else {
      andateNonValide.set(c.id, motivo ?? `a bordo della corsa con cui arriva ci sono già ${MAX_PASSEGGERI} passeggeri`);
    }
  }

  for (const c of corse) {
    if (c.esterna) continue;
    if (!c.catena) {
      stato.set(c.id, 'singolo');
      continue;
    }
    if (andateNonValide.has(c.id)) {
      stato.set(c.id, 'scoperto');
      scoperte.push(c.id);
      continue;
    }
    if (chiude(c)) {
      stato.set(c.id, 'chiude');
      continue;
    }
    if (c.rientro) {
      const t = perId.get(c.rientro);
      const motivo = motivoRientro(c, t, origine(c.id), t ? origine(t.id) : undefined, !!t && padre.has(t.id));
      const presi = (passeggeri.get(c.rientro)?.length ?? 0) + (andate.get(c.rientro)?.length ?? 0) + (t?.postiPresi ?? 0);
      if (!motivo && presi < MAX_PASSEGGERI) {
        passeggeri.set(c.rientro, [...(passeggeri.get(c.rientro) ?? []), c.id]);
        stato.set(c.id, 'a_bordo');
        continue;
      }
      rientriNonValidi.set(c.id, motivo ?? `a bordo ci sono già ${MAX_PASSEGGERI} passeggeri`);
      stato.set(c.id, 'scoperto');
      scoperte.push(c.id);
      continue;
    }
    if (figlio.has(c.id)) {
      stato.set(c.id, 'prosegue');
      continue;
    }
    stato.set(c.id, 'scoperto');
    scoperte.push(c.id);
  }

  return {
    padre, figlio, radice, stato, passeggeri, rientriNonValidi, andate, andateNonValide, scoperte,
    origine: (id) => origine(id),
  };
}

/** Perché il driver di `c` non può arrivare a bordo di `t` (null = può). */
export function motivoAndata(c, t) {
  if (!t || t.id === c.id) return 'la corsa con cui arriva il driver non c\'è più';
  if (t.esterna) return 'si arriva a bordo solo di una corsa dello stesso spostamento';
  if (!c.catena) return 'arriva a bordo di un\'altra corsa solo il driver di una corsa a catena';
  if (!t.catena) return 'la corsa con cui arriva il driver è singola';
  if (!t.a || t.a !== c.da) return 'la corsa con cui arriva il driver non arriva da dove parte questa';
  const tf = t.fine ?? null;
  const ci = c.inizio ?? null;
  if (tf !== null && tf !== '' && ci !== null && ci !== '' && tf > ci) {
    return 'la corsa con cui arriva il driver arriva dopo questa partenza';
  }
  return null;
}

/** Perché il driver di `c` non può rientrare a bordo di `t` (null = può). */
export function motivoRientro(c, t, origineC, origineT, tSegueCatena = false) {
  if (!t || t.id === c.id) return 'la corsa di rientro non c\'è più';
  if (!t.catena) {
    // Col JOLLY di un singolo che prosegue una catena: lo prende dove arriva
    // il singolo e lo riporta alla base della catena.
    if (!tSegueCatena) return 'la corsa di rientro è singola';
    if (t.mezzi) return 'la corsa di rientro non ha il jolly (rientro coi mezzi pubblici)';
    if (!c.a || t.a !== c.a) return 'il jolly della corsa di rientro non passa da dove arriva il driver';
    const tf = t.fine ?? null;
    const cf2 = c.fine ?? null;
    if (tf !== null && tf !== '' && cf2 !== null && cf2 !== '' && cf2 > tf) {
      return 'il driver arriva dopo la corsa di rientro: il jolly è già ripartito';
    }
    if (origineC && origineT && origineC !== origineT) {
      return 'la corsa di rientro non lo riporta da dove era partito';
    }
    return null;
  }
  if (t.da !== c.a) return 'la corsa di rientro non parte da dove arriva il driver';
  const ti = t.inizio ?? null;
  const cf = c.fine ?? null;
  if (ti !== null && ti !== '' && cf !== null && cf !== '' && ti < cf) {
    return 'la corsa di rientro parte prima che il driver arrivi';
  }
  if (origineC && origineT && origineC !== origineT) {
    return 'la corsa di rientro non lo riporta da dove era partito';
  }
  return null;
}

/**
 * Le corse con cui può rientrare a bordo il driver di `id`, con i posti
 * ancora liberi: [{ id, liberi }].
 */
export function opzioniRientro(corse, id) {
  const ipotesi = corse.map((c) => (c.id === id ? { ...c, rientro: '?' } : c));
  const a = analizzaCatene(ipotesi);
  const c = ipotesi.find((x) => x.id === id);
  if (!c) return [];
  return ipotesi
    .filter((t) => !motivoRientro(c, t, a.origine(c.id), a.origine(t.id), a.padre.has(t.id)))
    .map((t) => ({
      id: t.id,
      liberi: MAX_PASSEGGERI - (a.passeggeri.get(t.id)?.length ?? 0) - (a.andate.get(t.id)?.length ?? 0) - (t.postiPresi ?? 0),
    }))
    .filter((o) => o.liberi > 0);
}

/**
 * La MAPPA della giornata, per il disegno: le SEDI (nodi) e gli spostamenti
 * delle auto fra una sede e l'altra (archi). Un arco per verso: le corse
 * fra le stesse due sedi nello stesso verso stanno sullo stesso arco.
 * Ogni corsa porta le sue etichette (`n`, `ora`, `oraFine`, `daNome`,
 * `aNome`, `auto`, `prova`, `mezzi`). In più: chi rientra a bordo e, come
 * avviso, i driver che restano senza rientro.
 */
export function vistaSedi(corse) {
  const analisi = analizzaCatene(corse);
  const ora = (c) => (c.inizio === null || c.inizio === undefined || c.inizio === '' ? Number.MAX_SAFE_INTEGER : c.inizio);
  const proprie = corse.filter((c) => !c.esterna).sort((x, y) => ora(x) - ora(y) || (x.n || 0) - (y.n || 0));
  const nodi = [];
  const indice = new Map();
  const nodo = (key, nome) => {
    if (!key || key === '|') return null;
    if (!indice.has(key)) {
      indice.set(key, nodi.length);
      nodi.push({ key, nome: nome || '…' });
    }
    return indice.get(key);
  };
  const archi = new Map();
  for (const c of proprie) {
    const da = nodo(c.da, c.daNome);
    const a = nodo(c.a, c.aNome);
    if (da === null || a === null || da === a) continue;
    const k = `${da}>${a}`;
    if (!archi.has(k)) archi.set(k, { da, a, corse: [] });
    archi.get(k).corse.push({
      id: c.id,
      n: c.n,
      ora: c.ora,
      oraFine: c.oraFine,
      auto: c.auto,
      prova: !!c.prova,
      catena: !!c.catena,
      mezzi: !!c.mezzi,
      aBordo: (analisi.passeggeri.get(c.id) || []).length + (analisi.andate.get(c.id) || []).length,
    });
  }
  const avvisi = [...new Set(analisi.scoperte
    .map((id) => proprie.find((c) => c.id === id))
    .filter(Boolean)
    .map((c) => `Corsa ${c.n}: il driver resta a ${c.aNome || 'destinazione'} senza rientro`))];
  return { nodi, archi: [...archi.values()], avvisi, corse: proprie.length };
}

// --- Spostamenti già salvati ------------------------------------------------
const hhmm = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const minutiDi = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.getHours() * 60 + d.getMinutes();
};
const nomeBreve = (p) => (p ? [p.address, p.street_number].filter(Boolean).join(' ') || p.city || '' : '');

/**
 * Le corse di uno spostamento salvato, pronte per `analizzaCatene` e
 * `vistaSedi`, numerate nell'ordine della giornata come nel dettaglio. Le
 * corse di altri spostamenti su cui rientra qualcuno di questo si aggiungono
 * come esterne (da `giornata`, se ci sono; altrimenti una sagoma che le dà
 * per buone: il server le ha già controllate).
 */
export function corseDaSpostamento(journey, giornata = [], nome = nomeBreve) {
  // Numerate come nel dettaglio: per orario, contando anche le eliminate
  // (che il dettaglio mostra), senza le quali però si analizza.
  const tutte = [...(journey?.routes || [])].sort((a, b) => new Date(a.dateStart) - new Date(b.dateStart));
  const vive = tutte.filter((r) => r.status !== 'ELIMINATA');
  const proprie = vive.map((r) => ({
    id: r.id,
    n: tutte.indexOf(r) + 1,
    da: chiaveLuogo(r.startPoint),
    a: chiaveLuogo(r.endPoint),
    daNome: nome(r.startPoint),
    aNome: nome(r.endPoint),
    inizio: minutiDi(r.dateStart),
    fine: minutiDi(r.dateEnd),
    ora: hhmm(r.dateStart),
    oraFine: hhmm(r.dateEnd),
    catena: r.two_operators === false,
    rientro: r.two_operators === false ? r.rientro_route_id || null : null,
    andata: r.two_operators === false ? r.andata_route_id || null : null,
    prova: r.immatricolato === false,
    // Singolo: Simba ha deciso che il driver rientra con i mezzi pubblici.
    mezzi: r.two_operators !== false && !!r.rientro_altrove,
    auto: [r.marca, r.modello, r.targa].filter(Boolean).join(' '),
  }));
  const altre = (giornata || [])
    .filter((j) => j && j.id !== journey?.id)
    .flatMap((j) => j.routes || []);
  const esterne = [];
  for (const c of proprie) {
    if (!c.rientro || proprie.some((x) => x.id === c.rientro)) continue;
    if (esterne.some((e) => e.id === c.rientro)) continue;
    const r = altre.find((x) => x.id === c.rientro);
    esterne.push({
      id: c.rientro,
      esterna: true,
      catena: true,
      da: r ? chiaveLuogo(r.startPoint) : c.a,
      a: r ? chiaveLuogo(r.endPoint) : '',
      inizio: r ? minutiDi(r.dateStart) : null,
      fine: r ? minutiDi(r.dateEnd) : null,
      ora: r ? hhmm(r.dateStart) : '',
      daNome: r ? nome(r.startPoint) : c.aNome,
    });
  }
  return [...proprie, ...esterne];
}

/**
 * Come rientra il driver di ogni corsa a catena, in parole: per i dettagli
 * della corsa. Mappa id -> { testo, tipo } (tipo 'ok' | 'bordo' | 'scoperto').
 */
export function rientriInParole(corse) {
  const a = analizzaCatene(corse);
  const perId = new Map(corse.map((c) => [c.id, c]));
  const nomeCorsa = (c) => (c?.esterna ? `la corsa delle ${c.ora || '…'} di un altro spostamento` : `la corsa ${c?.n ?? ''}`);
  const out = new Map();
  for (const c of corse) {
    if (c.esterna) continue;
    if (!c.catena) {
      // Chi rientra col jolly di questo singolo.
      const conLui = (a.passeggeri.get(c.id) || []).map((id) => perId.get(id)?.n).filter(Boolean);
      const anche = conLui.length
        ? { aBordo: conLui.length === 1
          ? `Il jolly riporta anche il driver della corsa ${conLui[0]}`
          : `Il jolly riporta anche i driver delle corse ${conLui.join(', ')}` }
        : {};
      if (c.mezzi) out.set(c.id, { tipo: 'ok', testo: 'Il driver rientra con i mezzi pubblici (senza jolly)' });
      else if (a.padre.has(c.id)) {
        // Prosegue una catena: il jolly parte da dove è partita la catena.
        const origine = perId.get(a.radice.get(c.id))?.daNome || 'la partenza';
        out.set(c.id, {
          tipo: 'ok',
          testo: `Il jolly raggiunge il driver a ${c.aNome || 'destinazione'} e lo riporta a ${origine}`,
          ...anche,
        });
      }
      continue;
    }
    const stato = a.stato.get(c.id);
    const chiave = a.origine(c.id);
    const origine = corse.find((x) => !x.esterna && x.da === chiave)?.daNome
      || perId.get(a.radice.get(c.id))?.daNome || 'la partenza';
    const figlio = perId.get(a.figlio.get(c.id));
    const passeggeri = (a.passeggeri.get(c.id) || []).map((id) => perId.get(id)?.n).filter(Boolean);
    const vanno = (a.andate.get(c.id) || []).map((id) => perId.get(id)?.n).filter(Boolean);
    const arriva = a.andate.has(c.andata) && (a.andate.get(c.andata) || []).includes(c.id) ? perId.get(c.andata) : null;
    let r;
    if (stato === 'chiude') r = { tipo: 'ok', testo: `Chiude la catena: riporta il driver a ${origine}` };
    else if (stato === 'prosegue') r = { tipo: 'ok', testo: `Il driver prosegue con ${nomeCorsa(figlio)}` };
    // «del» + «la corsa 3» = «della corsa 3»
    else if (stato === 'a_bordo' && perId.get(c.rientro) && !perId.get(c.rientro).catena) {
      r = { tipo: 'bordo', testo: `Il driver rientra con il jolly del${nomeCorsa(perId.get(c.rientro))}` };
    } else if (stato === 'a_bordo') r = { tipo: 'bordo', testo: `Il driver rientra a bordo del${nomeCorsa(perId.get(c.rientro))}` };
    else if (a.andateNonValide.has(c.id)) {
      r = { tipo: 'scoperto', testo: `Il driver non può arrivare a bordo: ${a.andateNonValide.get(c.id)}` };
    } else r = { tipo: 'scoperto', testo: `Il driver resta a ${c.aNome || 'destinazione'} senza rientro` };
    if (arriva) r = { ...r, arriva: `Il driver arriva a bordo del${nomeCorsa(arriva)}` };
    if (vanno.length) {
      r = {
        ...r,
        porta: vanno.length === 1
          ? `Porta a bordo il driver della corsa ${vanno[0]}, che poi ne guida un'altra`
          : `Porta a bordo i driver delle corse ${vanno.join(', ')}, che poi ne guidano un'altra`,
      };
    }
    if (passeggeri.length) {
      r = {
        ...r,
        aBordo: passeggeri.length === 1
          ? `Porta a bordo il driver della corsa ${passeggeri[0]}`
          : `Porta a bordo i driver delle corse ${passeggeri.join(', ')}`,
      };
    }
    out.set(c.id, r);
  }
  return out;
}
